import type { MaybeRef, Ref } from "vue"
import type { Media } from "@/models"
import { useIntervalFn } from "@vueuse/core"
import { computed, ref, toValue, watchEffect } from "vue"
import { MediaType } from "@/models"
import { usePagination } from "./usePagination"

export interface UseLightboxOptions {
  media?: Ref<Media[]>
  beforeHide?: () => void
}

const visible = ref<boolean>(false)
const lightboxMedia = ref<Media[]>([])

const mediaCount = computed(() => lightboxMedia.value.length ?? 1)

const pagination = usePagination({
  initialPerPage: 1,
  pageCount: mediaCount,
})

export function useLightbox({ media, beforeHide }: UseLightboxOptions = {}) {
  const refresh = (force?: Media[]) => (lightboxMedia.value = force ?? toValue(media) ?? [])

  watchEffect(() => {
    if (media?.value && media?.value?.length) {
      refresh()
    }
  })

  const show = (index?: MaybeRef<number>) => {
    pagination.page.value = toValue(index) ?? 1
    visible.value = true
  }

  const hide = () => {
    if (beforeHide)
      beforeHide()
    // eslint-disable-next-line ts/no-use-before-define
    pauseSlideshow()
    visible.value = false
  }

  const toggle = () => (visible.value = !visible.value)

  const remove = (index: number = 0) => {
    const media = lightboxMedia.value[index]

    lightboxMedia.value = lightboxMedia.value.filter(m => m.id !== media.id)
  }

  const handleMediaEnd = () => {
    if (pagination.canNext.value)
      pagination.next()
    else pagination.page.value = 1
  }

  const checkCurrentMediaAndProceed = () => {
    const currentMedia = lightboxMedia.value[pagination.page.value - 1]

    // Guard: If currentMedia is undefined (empty array or out of bounds), stop processing
    if (!currentMedia) {
      return
    }

    const videoElement = document.getElementById(`gpx-gallery-video-${currentMedia.id}`)

    // Check if it's not a video type or element doesn't exist
    if (currentMedia.type !== MediaType.Vid || !videoElement) {
      handleMediaEnd()
      return
    }

    // Validate that the element is actually an HTMLVideoElement instance with addEventListener
    if (!(videoElement instanceof HTMLVideoElement) || typeof videoElement.addEventListener !== "function") {
      handleMediaEnd()
      return
    }

    const videoDuration = videoElement.duration
    if (videoDuration && videoDuration < 5) {
      handleMediaEnd()
      return
    }

    videoElement.addEventListener("ended", handleMediaEnd, { once: true })
  }

  const {
    pause: pauseSlideshow,
    resume: resumeSlideshow,
    isActive: isSlideshowActive,
  } = useIntervalFn(() => checkCurrentMediaAndProceed(), 5000, { immediate: false })

  // Watch for empty media and stop slideshow automatically
  watchEffect(() => {
    if (lightboxMedia.value.length === 0 && isSlideshowActive.value) {
      pauseSlideshow()
    }
  })

  return {
    show,
    hide,
    toggle,
    visible,
    refresh,
    remove,
    startSlideshow: resumeSlideshow,
    stopSlideshow: pauseSlideshow,
    isSlideshowActive,
    media: lightboxMedia,
    pagination,
    handleMediaEnd,
  }
}
